/**
 * Form.io Runtime Patch & Self-Hosted Setup
 *
 * This module:
 * 1. Registers all Form.io dependencies from local npm packages (no CDN)
 * 2. Patches Form.io components to prevent crashes when tooltip, customClass, or other
 *    string properties are undefined
 * 3. Disables Form.io's CDN loading to prevent external resource requests
 *
 * This patch MUST be imported before any Form.io components are used.
 * It works by:
 * - Pre-registering libraries like flatpickr, Choices.js, etc.
 * - Wrapping component initialization to sanitize all properties
 */

// Use @formio/js which is the actual package used by @formio/react
import { Components as FormioComponents, Formio } from '@formio/js';

// Import dependencies that Form.io would normally load from CDN
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';
import SignaturePad from 'signature_pad';
import momentTimezone from 'moment-timezone';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

// All string properties that Form.io components might call .replace() on
const STRING_PROPS = [
  'tooltip', 'placeholder', 'description', 'prefix', 'suffix',
  'customClass', 'label', 'errorLabel', 'content', 'html', 'tag',
  'className', 'title', 'legend', 'header', 'footer', 'template',
  'customDefaultValue', 'calculateValue', 'customValidation',
  'customConditional', 'attrs', 'style', 'mask', 'inputMask',
  'displayMask', 'tabindex', 'autocomplete', 'errorMessage',
  'customErrorMessage', 'inputType', 'labelPosition', 'optionsLabelPosition',
  // Checkbox-specific properties
  'shortcut', 'labelWidth', 'labelMargin', 'inputWidth',
  // Additional properties that may be accessed with .replace()
  'customError', 'modalEdit', 'key', 'type', 'input', 'tableView',
  'dataGridLabel', 'validateOn', 'validate', 'conditional',
];

/**
 * Recursively sanitize a Form.io component and all nested components
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sanitizeComponent(component: any): void {
  if (!component || typeof component !== 'object') return;

  // Ensure all string properties are strings (not undefined/null)
  for (const prop of STRING_PROPS) {
    if (component[prop] === undefined || component[prop] === null) {
      component[prop] = '';
    }
  }

  // Special handling for shortcut - Form.io Checkbox component calls shortcut.replace()
  // and it needs to be a string even if not in STRING_PROPS
  if (component.shortcut === undefined || component.shortcut === null) {
    component.shortcut = '';
  }

  // Ensure values object exists for select-type components
  if (component.values === undefined) {
    component.values = [];
  }

  // Recursively sanitize nested components
  if (Array.isArray(component.components)) {
    for (const child of component.components) {
      sanitizeComponent(child);
    }
  }

  // Handle columns layout
  if (Array.isArray(component.columns)) {
    for (const column of component.columns) {
      if (Array.isArray(column.components)) {
        for (const child of column.components) {
          sanitizeComponent(child);
        }
      }
    }
  }

  // Handle rows layout (tables)
  if (Array.isArray(component.rows)) {
    for (const row of component.rows) {
      if (Array.isArray(row)) {
        for (const cell of row) {
          if (Array.isArray(cell.components)) {
            for (const child of cell.components) {
              sanitizeComponent(child);
            }
          }
        }
      }
    }
  }
}

/**
 * Register bundled libraries with Form.io so it doesn't try to load from CDN.
 * This makes the libraries available globally as Form.io expects.
 */
function registerBundledLibraries(): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any;

    // Register flatpickr for date/time components
    if (!win.flatpickr) {
      win.flatpickr = flatpickr;
    }

    // Register Choices.js for select components
    if (!win.Choices) {
      win.Choices = Choices;
    }

    // Register SignaturePad for signature components
    if (!win.SignaturePad) {
      win.SignaturePad = SignaturePad;
    }

    // Register moment-timezone for datetime handling
    if (!win.moment) {
      win.moment = momentTimezone;
    }

    // Register tippy.js for tooltips
    if (!win.tippy) {
      win.tippy = tippy;
    }

    console.debug('Form.io bundled libraries registered');
  } catch (error) {
    console.warn('Failed to register Form.io libraries:', error);
  }
}

/**
 * Disable Form.io CDN loading to prevent external resource requests.
 * This is important for security - we don't want to load external scripts/styles.
 */
function disableFormioCDN(): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formio = Formio as any;

    // If Form.io has a CDN object, disable it by setting empty URLs
    if (formio.cdn) {
      // Override all CDN URLs to empty strings to prevent loading
      const libsToDisable = [
        'ace', 'bootstrap', 'bootstrap4', 'bootstrap5', 'bootswatch',
        'bootstrap-icons', 'ckeditor', 'flatpickr', 'flatpickr-formio',
        'font-awesome', 'grid', 'moment-timezone', 'quill',
        'shortcut-buttons-flatpickr', 'uswds', 'js', 'core'
      ];

      for (const lib of libsToDisable) {
        if (formio.cdn[lib] !== undefined) {
          formio.cdn[lib] = '';
        }
      }

      // Override the buildUrl method to return empty strings
      if (formio.cdn.buildUrl) {
        formio.cdn.buildUrl = () => '';
      }

      console.debug('Form.io CDN loading disabled');
    }

    // Also disable the requireLibrary function that loads external resources
    if (formio.requireLibrary) {
      const originalRequireLibrary = formio.requireLibrary;
      formio.requireLibrary = function (
        name: string,
        property: string,
        src: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...args: any[]
      ) {
        // Block any requests to external CDNs
        if (
          typeof src === 'string' &&
          (src.includes('cdn.form.io') ||
            src.includes('cdn.test-form.io') ||
            src.includes('cdn.jsdelivr.net') ||
            src.includes('cdnjs.cloudflare.com'))
        ) {
          console.debug(`Blocked Form.io CDN request: ${src}`);
          return Promise.resolve(null);
        }
        return originalRequireLibrary.call(this, name, property, src, ...args);
      };
    }
  } catch (error) {
    console.warn('Failed to disable Form.io CDN:', error);
  }
}

/**
 * Patch the base Component class's constructor and key methods.
 * The crashes happen because components call .replace() on undefined string properties.
 */
function patchBaseComponent(): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const components = (FormioComponents as any).components;
    if (!components) return;

    // Get the base Component class - it's the parent of all others
    const Component = components.component;
    if (!Component?.prototype) {
      console.warn('Form.io base Component not found');
      return;
    }

    // Patch the constructor to sanitize component on creation
    if (!Component._patchedConstructor) {
      Component._patchedConstructor = true;
      const OriginalComponent = Component;

      // Create a wrapper that sanitizes component data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const PatchedComponent = function(this: any, ...args: any[]) {
        // args[0] is the component schema, args[1] is options, args[2] is data
        if (args[0]) {
          sanitizeComponent(args[0]);
        }
        return OriginalComponent.apply(this, args);
      };

      // Copy prototype and static properties
      PatchedComponent.prototype = OriginalComponent.prototype;
      Object.setPrototypeOf(PatchedComponent, OriginalComponent);

      // Replace in components registry
      components.component = PatchedComponent;
    }

    // Patch getFormattedTooltip - handle undefined tooltip property directly
    // This prevents crashes when tooltip is undefined and .replace() is called on it
    const originalGetFormattedTooltip = Component.prototype.getFormattedTooltip;
    if (originalGetFormattedTooltip && !Component.prototype._patchedGetFormattedTooltip) {
      Component.prototype._patchedGetFormattedTooltip = true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Component.prototype.getFormattedTooltip = function (this: any, ...args: any[]) {
        // CRITICAL: Ensure this.component.tooltip is a string before any operations
        // The crash happens when Form.io calls .replace() on undefined tooltip
        if (this.component) {
          if (this.component.tooltip === undefined || this.component.tooltip === null) {
            this.component.tooltip = '';
          }
          // Also sanitize other properties that might be accessed
          sanitizeComponent(this.component);
        }
        // If tooltip is still empty, return empty string to avoid .replace() on undefined
        if (!this.component?.tooltip) {
          return '';
        }
        return originalGetFormattedTooltip.apply(this, args);
      };
    }

    // Also patch renderTemplate - sanitize before calling original
    const originalRenderTemplate = Component.prototype.renderTemplate;
    if (originalRenderTemplate && !Component.prototype._patchedRenderTemplate) {
      Component.prototype._patchedRenderTemplate = true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Component.prototype.renderTemplate = function (this: any, ...args: any[]) {
        if (this.component) {
          sanitizeComponent(this.component);
        }
        // Let errors propagate - don't silently swallow them
        return originalRenderTemplate.apply(this, args);
      };
    }

    console.debug('Form.io base Component patched');
  } catch (error) {
    console.warn('Failed to patch base Component:', error);
  }
}

/**
 * Patch specific component types that have their own render methods
 * that call .replace() on string properties.
 */
function patchSpecificComponents(): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const components = (FormioComponents as any).components;
    if (!components) return;

    // Components known to have custom render methods that crash
    const problematicComponents = [
      'checkbox', 'selectboxes', 'radio', 'select', 'content',
      'htmlelement', 'button', 'textarea', 'textfield', 'number',
      'email', 'url', 'phoneNumber', 'password', 'datetime', 'day',
      'time', 'currency', 'signature', 'file', 'panel', 'fieldset',
      'well', 'columns', 'table', 'tabs', 'hidden', 'container',
      'datagrid', 'editgrid', 'tree', 'address', 'survey', 'recaptcha',
    ];

    for (const componentName of problematicComponents) {
      const ComponentClass = components[componentName];
      if (!ComponentClass?.prototype) continue;

      // Patch render method to sanitize BEFORE calling original
      const originalRender = ComponentClass.prototype.render;
      if (originalRender && !ComponentClass.prototype._patchedRenderSpecific) {
        ComponentClass.prototype._patchedRenderSpecific = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ComponentClass.prototype.render = function (this: any, ...args: any[]) {
          // CRITICAL: Sanitize component BEFORE render executes
          if (this.component) {
            sanitizeComponent(this.component);
          }
          // Let errors propagate - don't silently swallow them
          return originalRender.apply(this, args);
        };
      }
    }

    // Special patch for Checkbox component which has additional properties
    const CheckboxComponent = components.checkbox;
    if (CheckboxComponent?.prototype && !CheckboxComponent.prototype._patchedCheckboxSpecific) {
      CheckboxComponent.prototype._patchedCheckboxSpecific = true;

      // Override the render to deeply sanitize before calling original
      const originalCheckboxRender = CheckboxComponent.prototype.render;
      if (originalCheckboxRender) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        CheckboxComponent.prototype.render = function (this: any, ...args: any[]) {
          // Deeply sanitize all string properties that Checkbox.render might access
          if (this.component) {
            // Ensure shortcut is a string (Checkbox calls shortcut.replace)
            if (typeof this.component.shortcut !== 'string') {
              this.component.shortcut = '';
            }
            // Ensure label is a string
            if (typeof this.component.label !== 'string') {
              this.component.label = '';
            }
            // Ensure all other potential string props are strings
            sanitizeComponent(this.component);
          }
          return originalCheckboxRender.apply(this, args);
        };
      }

      // Patch getShortcutButtons if it exists (calls shortcut.replace)
      const originalGetShortcutButtons = CheckboxComponent.prototype.getShortcutButtons;
      if (originalGetShortcutButtons) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        CheckboxComponent.prototype.getShortcutButtons = function (this: any, ...args: any[]) {
          if (this.component) {
            if (typeof this.component.shortcut !== 'string') {
              this.component.shortcut = '';
            }
          }
          return originalGetShortcutButtons.apply(this, args);
        };
      }
    }

    console.debug('Form.io specific components patched');
  } catch (error) {
    console.warn('Failed to patch specific components:', error);
  }
}

/**
 * Apply runtime patches to Form.io to prevent undefined property crashes
 */
export function applyFormioPatches(): void {
  try {
    // First, register bundled libraries so Form.io finds them
    registerBundledLibraries();

    // Then disable CDN loading to prevent external requests
    disableFormioCDN();

    // CRITICAL: Patch the base Component class first
    // This patches getFormattedTooltip which is the source of the crash
    patchBaseComponent();

    // Patch specific component types that have custom render methods
    patchSpecificComponents();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const components = (FormioComponents as any).components;

    if (!components) {
      console.warn('Form.io components not found, patches not applied');
      return;
    }

    // Patch ALL component types' init and render methods
    for (const componentName of Object.keys(components)) {
      const ComponentClass = components[componentName];

      if (!ComponentClass?.prototype) continue;

      // Wrap the component's init method to sanitize schema
      const originalInit = ComponentClass.prototype.init;
      if (originalInit && !ComponentClass.prototype._patchedInit) {
        ComponentClass.prototype._patchedInit = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ComponentClass.prototype.init = function (this: any, ...args: any[]) {
          // Sanitize component schema before init
          if (this.component) {
            sanitizeComponent(this.component);
          }
          return originalInit.apply(this, args);
        };
      }

      // Wrap render to sanitize before calling original (if not already patched by patchSpecificComponents)
      const originalRender = ComponentClass.prototype.render;
      if (originalRender && !ComponentClass.prototype._patchedRender && !ComponentClass.prototype._patchedRenderSpecific) {
        ComponentClass.prototype._patchedRender = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ComponentClass.prototype.render = function (this: any, ...args: any[]) {
          // Sanitize before render
          if (this.component) {
            sanitizeComponent(this.component);
          }
          // Let errors propagate - don't silently swallow them
          return originalRender.apply(this, args);
        };
      }
    }

    // Also intercept Formio.createForm to sanitize the entire form schema
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const originalCreateForm = (Formio as any).createForm;
    if (originalCreateForm && !(Formio as any)._patchedCreateForm) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (Formio as any)._patchedCreateForm = true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (Formio as any).createForm = function (element: any, form: any, options: any) {
        // Sanitize entire form schema before creating
        if (form && typeof form === 'object') {
          if (Array.isArray(form.components)) {
            for (const component of form.components) {
              sanitizeComponent(component);
            }
          }
        }
        return originalCreateForm.call(this, element, form, options);
      };
    }

    console.debug('Form.io patches applied successfully to all component types');
  } catch (error) {
    console.error('Failed to apply Form.io patches:', error);
  }
}

// Auto-apply patches when this module is imported
applyFormioPatches();
